import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, BookOpen, Music, Users2, Trash2, Pencil, ArrowLeft } from "lucide-react";
import CsvImportButton from "@/pages/platform/CsvImportButton";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function PlatformShowLibrary() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", composer: "", book: "", tags: "", description: "" });

  const load = () => api.get("/show-library").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((x) =>
      (x.title || "").toLowerCase().includes(t) ||
      (x.composer || "").toLowerCase().includes(t) ||
      (x.tags || []).some((tg) => tg.toLowerCase().includes(t))
    );
  }, [items, q]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const { data } = await api.post("/platform/show-library", {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success(`${data.title} added`);
      setCreateOpen(false);
      setForm({ title: "", composer: "", book: "", tags: "", description: "" });
      await load();
      setSelectedId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const del = async (t) => {
    if (!confirm(`Delete "${t.title}" and all its characters/scenes?`)) return;
    try {
      await api.delete(`/platform/show-library/${t.id}`);
      toast.success(`${t.title} removed`);
      await load();
      if (selectedId === t.id) setSelectedId(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (selectedId) {
    return <TemplateEditor templateId={selectedId} onBack={async () => { await load(); setSelectedId(null); }} />;
  }

  return (
    <div className="space-y-6" data-testid="platform-show-library">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Platform Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl">Show Library</h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-xl">Templates of Characters, Scenes, and Songs that admins can import when setting up a production. Ships with a starter set — edit anything, add your own.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-template-btn" style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New show
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">New show template</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <FieldRow label="Title" testid="template-title-input" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <FieldRow label="Composer / Music" testid="template-composer-input" value={form.composer} onChange={(v) => setForm({ ...form, composer: v })} />
              <FieldRow label="Book / Lyrics" testid="template-book-input" value={form.book} onChange={(v) => setForm({ ...form, book: v })} />
              <FieldRow label="Tags (comma separated)" testid="template-tags-input" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="musical, comedy, sondheim" />
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label>
                <Textarea data-testid="template-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-[#121212] border-white/10" />
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setCreateOpen(false)} className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Cancel</button>
              <button data-testid="create-template-btn" onClick={create} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Create</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, composer, tag" className="pl-9 bg-[#0F0F0F] border-white/10 h-10" />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} of {items.length}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`}
               className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5 hover:border-gold/40 transition-colors cursor-pointer"
               onClick={() => setSelectedId(t.id)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display text-xl">{t.title}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{t.composer || "—"}</div>
              </div>
              <button
                data-testid={`delete-template-${t.id}`}
                onClick={(e) => { e.stopPropagation(); del(t); }}
                className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10 shrink-0"
              ><Trash2 className="w-4 h-4" /></button>
            </div>
            {t.description && <p className="mt-3 text-xs text-zinc-400 leading-relaxed line-clamp-3">{t.description}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 text-zinc-300"><Users2 className="w-3 h-3" /> {t.character_count} chars</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 text-zinc-300"><BookOpen className="w-3 h-3" /> {t.scene_count} scenes</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gold/30 text-gold"><Music className="w-3 h-3" /> {t.song_count} songs</span>
            </div>
            {t.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {t.tags.map((tg) => (
                  <span key={tg} className="text-[10px] uppercase tracking-widest text-zinc-500 border border-white/5 px-1.5 py-0.5 rounded">{tg}</span>
                ))}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-zinc-500 text-sm">
            No templates. Click <span className="text-gold">New show</span> above to create one.
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-zinc-300 text-xs uppercase tracking-widest">{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 bg-[#121212] border-white/10 h-11" />
    </div>
  );
}

// ---------- Detail editor ----------
function TemplateEditor({ templateId, onBack }) {
  const [t, setT] = useState(null);
  const [meta, setMeta] = useState({ title: "", composer: "", book: "", tags: "", description: "" });
  const [tab, setTab] = useState("characters");

  const load = async () => {
    const { data } = await api.get(`/show-library/${templateId}`);
    setT(data);
    setMeta({
      title: data.title || "",
      composer: data.composer || "",
      book: data.book || "",
      tags: (data.tags || []).join(", "),
      description: data.description || "",
    });
  };
  useEffect(() => { load(); }, [templateId]);

  const saveMeta = async () => {
    try {
      await api.patch(`/platform/show-library/${templateId}`, {
        ...meta,
        tags: meta.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (!t) return <div className="text-zinc-500 text-sm">Loading template…</div>;

  return (
    <div className="space-y-6" data-testid="platform-template-editor">
      <button data-testid="template-back-btn" onClick={onBack} className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Back to library
      </button>

      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldRow label="Title" testid="edit-template-title" value={meta.title} onChange={(v) => setMeta({ ...meta, title: v })} />
          <FieldRow label="Composer / Music" testid="edit-template-composer" value={meta.composer} onChange={(v) => setMeta({ ...meta, composer: v })} />
          <FieldRow label="Book / Lyrics" testid="edit-template-book" value={meta.book} onChange={(v) => setMeta({ ...meta, book: v })} />
          <FieldRow label="Tags (comma separated)" testid="edit-template-tags" value={meta.tags} onChange={(v) => setMeta({ ...meta, tags: v })} />
        </div>
        <div>
          <Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label>
          <Textarea data-testid="edit-template-description" rows={3} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className="mt-2 bg-[#121212] border-white/10" />
        </div>
        <div className="flex justify-end">
          <button data-testid="save-template-meta-btn" onClick={saveMeta} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Save details</button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10">
        {[
          { k: "characters", label: `Characters (${t.characters.length})`, icon: Users2 },
          { k: "scenes", label: `Scenes (${t.scenes.length})`, icon: BookOpen },
          { k: "songs", label: `Songs (${t.songs.length})`, icon: Music },
        ].map(({ k, label, icon: I }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${tab === k ? "border-gold text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
            <I className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "characters" && <CharactersTab template={t} onChange={load} />}
      {tab === "scenes" && <ScenesTab template={t} kind="scene" onChange={load} />}
      {tab === "songs" && <ScenesTab template={t} kind="song" onChange={load} />}
    </div>
  );
}

// ---------- Characters tab ----------
function CharactersTab({ template, onChange }) {
  const [name, setName] = useState("");
  const [voice, setVoice] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/platform/show-library/${template.id}/characters`, {
        name, description: desc, voice_type: voice, position: template.characters.length,
      });
      setName(""); setVoice(""); setDesc("");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!editing) return;
    try {
      await api.patch(`/platform/show-library/${template.id}/characters/${editing.id}`, {
        name: editing.name, description: editing.description || "", voice_type: editing.voice_type || "", position: editing.position || 0,
      });
      setEditing(null);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Remove this character?")) return;
    await api.delete(`/platform/show-library/${template.id}/characters/${id}`);
    onChange();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Add characters</div>
          <CsvImportButton templateId={template.id} kind="characters" onImported={onChange} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Input data-testid="new-character-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Character name" className="bg-[#121212] border-white/10 h-10" />
          <Input data-testid="new-character-voice" value={voice} onChange={(e) => setVoice(e.target.value)} placeholder="Voice type" className="bg-[#121212] border-white/10 h-10" />
          <Input data-testid="new-character-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" className="bg-[#121212] border-white/10 h-10 md:col-span-1" />
          <button data-testid="add-character-btn" onClick={add} disabled={busy || !name.trim()} style={goldBtn} className="h-10 rounded font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#121212] text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Voice type</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {template.characters.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`template-char-row-${c.id}`}>
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-zinc-400">{c.voice_type || "—"}</td>
                <td className="p-3 text-zinc-400">{c.description || "—"}</td>
                <td className="p-3 text-right">
                  <button data-testid={`edit-char-${c.id}`} onClick={() => setEditing({ ...c })} className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`del-char-${c.id}`} onClick={() => del(c.id)} className="p-1.5 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {template.characters.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-zinc-500">No characters yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit character</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 pt-2">
              <FieldRow label="Name" testid="edit-char-name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <FieldRow label="Voice type" testid="edit-char-voice" value={editing.voice_type || ""} onChange={(v) => setEditing({ ...editing, voice_type: v })} />
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Description</Label>
                <Textarea data-testid="edit-char-desc" rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-2 bg-[#121212] border-white/10" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Cancel</button>
            <button data-testid="save-char-btn" onClick={save} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Scenes / Songs tab (same shape) ----------
function ScenesTab({ template, kind, onChange }) {
  const isSong = kind === "song";
  const list = isSong ? template.songs : template.scenes;
  const [form, setForm] = useState({ name: "", act: "1", pages: "", notes: "", characters: "" });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/platform/show-library/${template.id}/scenes`, {
        name: form.name, act: form.act || "1", is_song: isSong,
        characters: form.characters.split(",").map((s) => s.trim()).filter(Boolean),
        pages: form.pages, notes: form.notes,
        position: list.length,
      });
      setForm({ name: "", act: "1", pages: "", notes: "", characters: "" });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!editing) return;
    try {
      await api.patch(`/platform/show-library/${template.id}/scenes/${editing.id}`, {
        name: editing.name, act: editing.act || "1", is_song: isSong,
        characters: (editing.characters_raw != null ? editing.characters_raw : (editing.characters || []).join(", "))
          .split(",").map((s) => s.trim()).filter(Boolean),
        pages: editing.pages || "", notes: editing.notes || "", position: editing.position || 0,
      });
      setEditing(null);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm(`Remove this ${kind}?`)) return;
    await api.delete(`/platform/show-library/${template.id}/scenes/${id}`);
    onChange();
  };

  const byAct = list.reduce((acc, s) => { (acc[s.act] ||= []).push(s); return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Add {kind}s</div>
          <CsvImportButton templateId={template.id} kind={isSong ? "songs" : "scenes"} onImported={onChange} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <Input data-testid={`new-${kind}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isSong ? "Song title" : "Scene name"} className="bg-[#121212] border-white/10 h-10 md:col-span-2" />
          <Input value={form.act} onChange={(e) => setForm({ ...form, act: e.target.value })} placeholder="Act" className="bg-[#121212] border-white/10 h-10" />
          <Input value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} placeholder="Pages" className="bg-[#121212] border-white/10 h-10" />
          <Input value={form.characters} onChange={(e) => setForm({ ...form, characters: e.target.value })} placeholder="Characters (comma sep)" className="bg-[#121212] border-white/10 h-10" />
          <button data-testid={`add-${kind}-btn`} onClick={add} disabled={busy || !form.name.trim()} style={goldBtn} className="h-10 rounded font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      {Object.keys(byAct).sort().map((act) => (
        <div key={act}>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">Act {act}</div>
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {byAct[act].map((s) => (
                  <tr key={s.id} className="border-t border-white/5 first:border-t-0" data-testid={`template-${kind}-row-${s.id}`}>
                    <td className="p-3 w-10">
                      {isSong ? <Music className="w-4 h-4 text-gold" /> : <BookOpen className="w-4 h-4 text-zinc-400" />}
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{s.name}</div>
                      {s.pages && <div className="text-[11px] text-zinc-500">Pages {s.pages}</div>}
                      {s.characters?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {s.characters.slice(0, 6).map((c, i) => (
                            <span key={i} className="text-[10px] text-zinc-400 border border-white/5 px-1.5 py-0.5 rounded">{c}</span>
                          ))}
                          {s.characters.length > 6 && <span className="text-[10px] text-zinc-500">+{s.characters.length - 6}</span>}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right w-24">
                      <button data-testid={`edit-${kind}-${s.id}`} onClick={() => setEditing({ ...s, characters_raw: (s.characters || []).join(", ") })} className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`del-${kind}-${s.id}`} onClick={() => del(s.id)} className="p-1.5 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {list.length === 0 && (
        <div className="rounded-lg border border-white/10 p-8 text-center text-zinc-500 text-sm">No {kind}s yet.</div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit {kind}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 pt-2">
              <FieldRow label={isSong ? "Song title" : "Scene name"} testid={`edit-${kind}-name`} value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <div className="grid grid-cols-2 gap-2">
                <FieldRow label="Act" testid={`edit-${kind}-act`} value={editing.act || "1"} onChange={(v) => setEditing({ ...editing, act: v })} />
                <FieldRow label="Pages" testid={`edit-${kind}-pages`} value={editing.pages || ""} onChange={(v) => setEditing({ ...editing, pages: v })} />
              </div>
              <FieldRow label="Characters (comma sep)" testid={`edit-${kind}-chars`} value={editing.characters_raw ?? (editing.characters || []).join(", ")} onChange={(v) => setEditing({ ...editing, characters_raw: v })} />
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Notes</Label>
                <Textarea data-testid={`edit-${kind}-notes`} rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="mt-2 bg-[#121212] border-white/10" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Cancel</button>
            <button data-testid={`save-${kind}-btn`} onClick={save} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
